import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchMilestoneLogs,
  acceptMilestoneLog,
  rejectMilestoneLog,
} from "../../actions/referralActions";
import useHasPermission from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissions";
import { downloadBtnStyle, downloadCSV, getDynamicKeys, toTitleCase, modalOverlay, modalBox, modalHeader, modalBody, closeBtn } from "./shared";

const MilestoneLogs = () => {
  const dispatch = useDispatch();
  const canApprove = useHasPermission(PERMISSIONS.APPROVE_REFERRAL_REWARDS);
  const canReject = useHasPermission(PERMISSIONS.REJECT_REFERRAL_REWARDS);

  const [logsPage, setLogsPage] = useState(1);
  const [filterReferrerId, setFilterReferrerId] = useState("");
  const [filterStatusId, setFilterStatusId] = useState("");
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const { loading, data, error } = useSelector((state) => state.getMilestoneLogs) || {};
  const logs = data?.data;
  const dynamicKeys = getDynamicKeys(logs?.items || []);

  const extraColumns = (canApprove || canReject) ? 1 : 0;
  const totalCols = Math.max(dynamicKeys.length, 1) + extraColumns;

  const handleFilter = () => {
    setLogsPage(1);
    dispatch(fetchMilestoneLogs(1, 100, filterReferrerId || null, filterStatusId || null));
  };

  const handlePageChange = (page) => {
    setLogsPage(page);
    dispatch(fetchMilestoneLogs(page, 100, filterReferrerId || null, filterStatusId || null));
  };

  const handleDownload = () => {
    if (!logs?.items?.length) {
      toast.warning("No data to download");
      return;
    }
    downloadCSV(
      "milestone_logs",
      dynamicKeys.map(toTitleCase),
      logs.items.map((item) => dynamicKeys.map((k) => item[k] ?? "")),
    );
  };

  const handleAccept = async (id) => {
    setActionLoading(true);
    try {
      await dispatch(acceptMilestoneLog(id));
      toast.success("Milestone log accepted");
      dispatch(fetchMilestoneLogs(logsPage, 100, filterReferrerId || null, filterStatusId || null));
    } catch (err) {
      toast.error(err?.message || "Failed to accept");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (id) => {
    setRejectingId(id);
    setRejectReason("");
    setRejectModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeRejectModal = () => {
    setRejectModal(false);
    setRejectingId(null);
    setRejectReason("");
    document.body.style.overflow = "auto";
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    setActionLoading(true);
    try {
      await dispatch(rejectMilestoneLog(rejectingId, rejectReason.trim()));
      toast.success("Milestone log rejected");
      closeRejectModal();
      dispatch(fetchMilestoneLogs(logsPage, 100, filterReferrerId || null, filterStatusId || null));
    } catch (err) {
      toast.error(err?.message || "Failed to reject");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;

  return (
    <>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Referrer ID</label>
          <input value={filterReferrerId} onChange={(e) => setFilterReferrerId(e.target.value)} placeholder="Filter by referrer ID" style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px", width: "180px" }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "4px" }}>Status ID</label>
          <input value={filterStatusId} onChange={(e) => setFilterStatusId(e.target.value)} placeholder="Filter by status ID" style={{ padding: "8px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "13px", width: "160px" }} />
        </div>
        <button onClick={handleFilter} style={{ padding: "8px 18px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "13px" }}>
          Filter
        </button>
        <button onClick={handleDownload} style={{ ...downloadBtnStyle, marginLeft: "auto" }}>
          ⬇ Download CSV
        </button>
      </div>

      <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
        <MDBTableHead>
          <tr>
            {dynamicKeys.length > 0 ? (
              dynamicKeys.map((key) => <th key={key}>{toTitleCase(key)}</th>)
            ) : (
              <th>No Data</th>
            )}
            {(canApprove || canReject) && <th>Actions</th>}
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {logs?.items?.length > 0 ? (
            logs.items.map((item, idx) => (
              <tr key={item.id || idx}>
                {dynamicKeys.map((key) => (
                  <td key={key}>{item[key] != null ? String(item[key]) : "-"}</td>
                ))}
                {(canApprove || canReject) && (
                  <td>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {canApprove && (
                        <button onClick={() => handleAccept(item.id)} disabled={actionLoading} style={{ padding: "4px 12px", background: "#28a745", color: "#fff", border: "none", borderRadius: "4px", cursor: actionLoading ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "600" }}>
                          Accept
                        </button>
                      )}
                      {canReject && (
                        <button onClick={() => openRejectModal(item.id)} disabled={actionLoading} style={{ padding: "4px 12px", background: "#dc3545", color: "#fff", border: "none", borderRadius: "4px", cursor: actionLoading ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: "600" }}>
                          Reject
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={totalCols} style={{ textAlign: "center", padding: "20px" }}>No milestone logs found</td>
            </tr>
          )}
        </MDBTableBody>
      </MDBTable>

      {logs?.page?.total_page > 1 && (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
          {Array.from({ length: logs.page.total_page }, (_, i) => i + 1).map((page) => (
            <button key={page} onClick={() => handlePageChange(page)} style={{ padding: "6px 12px", border: "1px solid #444", borderRadius: "4px", background: logsPage === page ? "#007bff" : "#1a1a1a", color: "#fff", cursor: "pointer" }}>
              {page}
            </button>
          ))}
        </div>
      )}

      {rejectModal && (
        <div style={modalOverlay} onClick={closeRejectModal}>
          <div style={{ ...modalBox, maxWidth: "420px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>Reject Milestone Log</h3>
              <button style={closeBtn} onClick={closeRejectModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleRejectSubmit}>
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "12px", color: "#aaa", marginBottom: "6px", fontWeight: "500" }}>Reason *</label>
                  <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Provide a reason for rejection" style={{ width: "100%", padding: "10px 12px", border: "1px solid #444", borderRadius: "6px", background: "#1a1a1a", color: "#fff", fontSize: "14px", minHeight: "100px", resize: "vertical" }} required />
                </div>
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button type="button" onClick={closeRejectModal} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={actionLoading} style={{ padding: "10px 20px", background: "#dc3545", color: "#fff", border: "none", borderRadius: "6px", cursor: actionLoading ? "not-allowed" : "pointer", fontWeight: "600", opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? "Rejecting..." : "Reject"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MilestoneLogs;
